import { SiteList, SiteListItem } from '../../types'
import { motion } from 'framer-motion'
import { Globe, ExternalLink, Star, Search, Filter, TrendingUp } from 'lucide-react'
import { useState, useMemo } from 'react'

interface SiteListPlannerProps {
  data: SiteList | SiteListItem[]
}

export function SiteListPlanner({ data }: SiteListPlannerProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'impressions' | 'category' | 'url'>('impressions')

  // Normalize data to always work with flat array
  const siteItems: SiteListItem[] = useMemo(() => {
    if (!data) return []
    // If data is already an array of SiteListItem
    if (Array.isArray(data)) {
      return data
    }
    // If data has a sites array (new format)
    if (data.sites && Array.isArray(data.sites)) {
      return data.sites
    }
    // If data has categories (legacy format) - flatten all sites
    if (data.categories && Array.isArray(data.categories)) {
      return data.categories.flatMap(cat => 
        cat.sites.map(site => ({
          url: site.url,
          category: site.category || cat.name,
          potential_impressions: site.reach || '0'
        }))
      )
    }
    return []
  }, [data])

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>()
    siteItems.forEach(site => {
      if (site.category) {
        // Split categories that have multiple values (e.g., "News, Lifestyle")
        site.category.split(',').forEach(c => cats.add(c.trim()))
      }
    })
    return Array.from(cats).sort()
  }, [siteItems])

  // Parse impressions to number for sorting
  const parseImpressions = (imp: string | number): number => {
    if (typeof imp === 'number') return imp
    if (imp === '< 1000' || imp === '<1000') return 500
    const num = parseInt(imp.replace(/,/g, ''), 10)
    return isNaN(num) ? 0 : num
  }

  // Format impressions for display
  const formatImpressions = (imp: string | number): string => {
    if (typeof imp === 'string' && imp.includes('<')) return imp
    const num = parseImpressions(imp)
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  // Filter and sort sites
  const filteredSites = useMemo(() => {
    return siteItems
      .filter(site => {
        const matchesSearch = searchTerm === '' || 
          site.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
          site.category?.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesCategory = selectedCategory === 'all' || 
          site.category?.includes(selectedCategory)
        return matchesSearch && matchesCategory
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'impressions':
            return parseImpressions(b.potential_impressions) - parseImpressions(a.potential_impressions)
          case 'category':
            return (a.category || '').localeCompare(b.category || '')
          case 'url':
            return a.url.localeCompare(b.url)
          default:
            return 0
        }
      })
  }, [siteItems, searchTerm, selectedCategory, sortBy])

  // Get impression level color
  const getImpressionColor = (imp: string | number): string => {
    const num = parseImpressions(imp)
    if (num >= 100000) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    if (num >= 10000) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    if (num >= 1000) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
  }

  if (!data || siteItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Globe className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No site list available</p>
        </div>
      </div>
    )
  }

  // Calculate stats
  const totalImpressions = siteItems.reduce((sum, site) => sum + parseImpressions(site.potential_impressions), 0)
  const highImpactSites = siteItems.filter(site => parseImpressions(site.potential_impressions) >= 100000).length

  return (
    <div className="space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg shadow-sm p-4 lg:p-5"
      >
        <h2 className="text-lg lg:text-xl font-bold text-white mb-2">Site List Planner</h2>
        <p className="text-sm text-gray-300 mb-4">Curated list of premium placements for your campaign</p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center text-gray-400 text-xs mb-1">
              <Globe className="h-3.5 w-3.5 mr-1.5" />
              Total Sites
            </div>
            <div className="text-xl font-bold text-white">{siteItems.length}</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center text-gray-400 text-xs mb-1">
              <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
              Total Impressions
            </div>
            <div className="text-xl font-bold text-white">{formatImpressions(totalImpressions)}</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center text-gray-400 text-xs mb-1">
              <Star className="h-3.5 w-3.5 mr-1.5" />
              High Impact Sites
            </div>
            <div className="text-xl font-bold text-white">{highImpactSites}</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center text-gray-400 text-xs mb-1">
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Categories
            </div>
            <div className="text-xl font-bold text-white">{categories.length}</div>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
      >
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by URL or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.slice(0, 20).map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="impressions">Sort by Impressions</option>
            <option value="category">Sort by Category</option>
            <option value="url">Sort by URL</option>
          </select>
        </div>
        
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredSites.length} of {siteItems.length} sites
        </div>
      </motion.div>

      {/* Site List Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400 font-medium">Site URL</th>
                <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400 font-medium">Category</th>
                <th className="text-right py-3 px-4 text-gray-600 dark:text-gray-400 font-medium">Potential Impressions</th>
                <th className="text-center py-3 px-4 text-gray-600 dark:text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSites.slice(0, 100).map((site, index) => (
                <tr 
                  key={`${site.url}-${index}`} 
                  className="border-b border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-gray-900 dark:text-white font-medium truncate max-w-xs">
                        {site.url}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-600 dark:text-gray-400 text-xs">
                      {site.category?.split('/').slice(0, 2).join(' › ') || 'N/A'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getImpressionColor(site.potential_impressions)}`}>
                      {formatImpressions(site.potential_impressions)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <a
                      href={`https://${site.url.replace(/^https?:\/\//, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Visit
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredSites.length > 100 && (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-slate-700">
            Showing first 100 sites. Use search/filter to narrow down results.
          </div>
        )}
      </motion.div>

      {/* Category Summary */}
      {categories.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
        >
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Filter className="h-5 w-5 mr-2 text-primary-500" />
            Category Distribution
          </h3>
          <div className="flex flex-wrap gap-2">
            {categories.slice(0, 30).map(cat => {
              const count = siteItems.filter(s => s.category?.includes(cat)).length
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(selectedCategory === cat ? 'all' : cat)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    selectedCategory === cat 
                      ? 'bg-primary-500 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-gray-300 dark:hover:bg-slate-700'
                  }`}
                >
                  {cat} ({count})
                </button>
              )
            })}
          </div>
        </motion.div>
      )}
    </div>
  )
}
